import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { first, last } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { DatapointLookupActions } from './datapoint-lookup.actions';
import { WebSocketEntities } from '../../features/shared/models/web-socket/web-socket.models';
import { DatapointLookupService } from '../../features/unified-datapoint/services/datapoint-lookup.service';
import { RootState } from '../root-state.model';

// Entity-channel payloads look like `tabId:TRIGGER`, user-channel ones like `entity/id|tabId:TRIGGER` —
// only the latter carry an entity id, so anything non-ObjectId extracted here must be ignored.
const OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;

@Injectable()
export class DatapointLookupEffects {
  private service = inject(DatapointLookupService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);

  private updater$ = this.service
    .listenOnChanges$()
    .pipe()
    .subscribe((change) => {
      const id = first(last(change.message.split('/')).split('|'));

      if (id && OBJECT_ID_PATTERN.test(id)) {
        if (change.type === WebSocketEntities.Topic) {
          this.store.dispatch(DatapointLookupActions.LoadTopicLookup({ id }));
        } else if (change.type === WebSocketEntities.Formula) {
          this.store.dispatch(DatapointLookupActions.LoadFormulaLookup({ id }));
        }
      }
    });

  loadLookupEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointLookupActions.LoadLookup),
      switchMap((action) => {
        return this.service.getLookup().pipe(
          map((lookup) => DatapointLookupActions.LoadLookupSuccess({ lookup })),
          catchError((error) => of(DatapointLookupActions.LoadLookupFail({ error })))
        );
      })
    )
  );

  loadFormulaLookupEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointLookupActions.LoadFormulaLookup),
      switchMap((action) => {
        return this.service.getFormulaLookupEntry(action.id).pipe(
          map((lookup) => DatapointLookupActions.LoadFormulaLookupSuccess({ lookup })),
          catchError((error) => of(DatapointLookupActions.LoadFormulaLookupFail({ error })))
        );
      })
    )
  );

  loadTopicLookupEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DatapointLookupActions.LoadTopicLookup),
      switchMap((action) => {
        return this.service.getTopicLookupEntry(action.id).pipe(
          map((lookup) => DatapointLookupActions.LoadTopicLookupSuccess({ lookup })),
          catchError((error) => of(DatapointLookupActions.LoadTopicLookupFail({ error })))
        );
      })
    )
  );
}
